import React, { useState, useEffect } from 'react';
import { useLogin, useNotify, useRedirect, useAuthState } from 'react-admin';
import { Box, Button, Card, CardContent, TextField, Typography, Divider, CircularProgress, Avatar } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

const API_URL = import.meta.env['VITE_API_URL'] || 'http://localhost:8000';
const SHOW_EMAIL_LOGIN = import.meta.env['VITE_SHOW_EMAIL_LOGIN'] === 'true';
const DEFAULT_REDIRECT_PATH = '/admin/admin-users';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const login = useLogin();
  const notify = useNotify();
  const redirect = useRedirect();
  const { isLoading: checkingAuth, authenticated } = useAuthState();

  // Already signed in with a valid token → don't show the login form, send them
  // straight into the app. (checkAuth in the auth provider resolves for a valid token.)
  useEffect(() => {
    if (!checkingAuth && authenticated) {
      redirect(DEFAULT_REDIRECT_PATH);
    }
  }, [checkingAuth, authenticated, redirect]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login({ username: email, password });
    } catch (error) {
      notify('Invalid email or password', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // check for oauth error
  useEffect(() => {
    const oauthError = sessionStorage.getItem('oauth_error');
    if (oauthError) {
      sessionStorage.removeItem('oauth_error');
      notify(oauthError, { type: 'error' });
    }
  }, []);

  const handleMicrosoftLogin = () => {
    // show loading spinner
    setLoading(true);
    // Redirect to your backend OAuth endpoint (under /admin so it routes through the gateway)
    window.location.href = `${API_URL}/admin/oauth/microsoft`;
  };

  if (loading || checkingAuth || authenticated) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
        }}
      >
        <CircularProgress sx={{ color: 'white' }} />
        <Typography sx={{ mt: 2, color: 'white' }}>Signing in...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'radial-gradient(circle at 50% 14em, rgb(49, 50, 100) 0%, rgb(0, 2, 59) 60%, rgb(0, 2, 59) 100%);',
      }}
    >
      <Card sx={{ minWidth: 360, maxWidth: 400, borderRadius: 2, boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)' }}>
        <CardContent sx={{ px: 4, py: 5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56, mb: 2 }}>
              <LockOutlinedIcon />
            </Avatar>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
              C360 Admin Portal
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Sign in to continue
            </Typography>
          </Box>

          {/* Microsoft SSO Button */}
          <Button
            fullWidth
            variant="contained"
            onClick={handleMicrosoftLogin}
            sx={{ py: 1.5 }}
            startIcon={
              <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21">
                <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
              </svg>
            }
          >
            Sign in with Microsoft
          </Button>

          {SHOW_EMAIL_LOGIN && (
            <>
              <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  or
                </Typography>
              </Divider>

              {/* Standard Email/Password Form */}
              <Box component="form" onSubmit={handleSubmit}>
                <TextField fullWidth label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} margin="normal" required autoComplete="email" />
                <TextField fullWidth label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} margin="normal" required autoComplete="current-password" />
                <Button type="submit" fullWidth variant="outlined" sx={{ mt: 2, py: 1.5 }} disabled={loading}>
                  Sign in with Email
                </Button>
              </Box>
            </>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 4 }}>
            Use your organization Microsoft account to continue.
          </Typography>
        </CardContent>
      </Card>
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mt: 3 }}>
        &copy; {new Date().getFullYear()} C360 &middot; All rights reserved
      </Typography>
    </Box>
  );
};

export default LoginPage;
